import {
  useEffect,
  useState,
} from 'react'
import {
  useFeatureFlagEnabled,
} from 'posthog-js/react'
import {
  contactTextingService,
} from '../services/api'
import {
  FeatureFlags,
} from '../analytics/posthog'

// The opt-in the shared guest line's A2P campaign quotes (GET-18126): until an
// owner ticks it, the assistant texts none of their contacts from that line.
// Carriers reject consent that isn't its own unchecked-by-default control, and
// the campaign registration quotes this label word for word, so changing it
// means re-registering the campaign.
export const CONTACT_TEXTING_CONSENT_LABEL =
  'Let my assistant text my business contacts for me. I confirm the people it '
  + 'texts gave me their number for business and expect to hear from me, and '
  + 'that it texts someone only when they text it first or when I ask it to '
  + 'reach that person. Every first text names Catch and me, and says to reply '
  + 'STOP to opt out.'

export function ContactTextingConsent() {
  const enabled = useFeatureFlagEnabled(FeatureFlags.GUEST_SMS_POOL) === true
  const [consentedAt, setConsentedAt] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [ticked, setTicked] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled) return
    contactTextingService.getConsent()
      .then(setConsentedAt)
      .catch(() => setError('Couldn’t load this setting just now.'))
      .finally(() => setLoaded(true))
  }, [enabled])

  if (!enabled || !loaded) return null

  const save = async (consent: boolean) => {
    setSaving(true)
    setError(null)
    try {
      setConsentedAt(await contactTextingService.setConsent(consent))
      setTicked(false)
    } catch {
      setError('That didn’t save. Try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-base font-semibold text-gray-900 mb-2">Texting your contacts</h3>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {consentedAt ? (
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-gray-700">
            On since {new Date(consentedAt).toLocaleDateString()}. Your assistant
            can text your business contacts for you.
          </p>
          <button
            onClick={() => void save(false)}
            disabled={saving}
            className="shrink-0 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors disabled:opacity-50"
          >
            Turn off
          </button>
        </div>
      ) : (
        <>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={ticked}
              onChange={(e) => setTicked(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-primary-500 focus:ring-primary-500 cursor-pointer"
            />
            <span className="text-sm leading-relaxed text-gray-700">
              {CONTACT_TEXTING_CONSENT_LABEL}
            </span>
          </label>
          <button
            onClick={() => void save(true)}
            disabled={!ticked || saving}
            className="mt-3 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Turn on
          </button>
        </>
      )}
    </div>
  )
}
